import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { 
  Box, 
  CssBaseline, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import NotificationCenter from './components/notifications/NotificationCenter';
import NotificationTicker from './components/notifications/NotificationTicker';
import ProductMaster from './components/ProductMaster';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import ProductSupplierView from './components/ProductSupplierView';
import ConfigManagement from './components/ConfigManagement';
import ForecastRuns from './components/ForecastRuns';
import ForecastCanvas from './components/ForecastingHierarchy/ForecastCanvas';
import NodeDetailsPage from './components/ForecastingHierarchy/NodeDetailsPage';
import Orders from './components/Orders';
import Inventory from './components/Inventory';
import InventorySkuLot from './components/InventorySkuLot';
import WarehouseInventory from './components/WarehouseInventory';
import NetworkMap from './components/NetworkMap';
import InstallBaseMap from './components/Install-base/InstallBaseMap';
import InstallationDetails from './components/Install-base/InstallationDetails';
import Logo from './components/Logo';
import InventoryOptimization from './components/InventoryOptimization';
import ShipmentTrackingPage from './components/ShipmentTracking/ShipmentTrackingPage';
import ProductHierarchyPage from './components/ProductHierarchy/ProductHierarchyPage';
import InventoryHierarchyPage from './components/InventoryHierarchy/InventoryHierarchyPage';
import './styles/App.css'; // Import the CSS

import DeveloperDashboard from './developers/Dashboard/DeveloperDashboard';
import ApiTestPage from './developers/ApiTestPage';
import { FeatureFlagProvider, useFeatureFlags, FEATURE_CODES } from './context/FeatureFlagContext';
import TenantManagement from './developers/TenantManagement';
import TenantFeatures from './developers/TenantFeatures';
import TenantCreate from './developers/TenantCreate';
import TenantEdit from './developers/TenantEdit';
import TenantProvisioning from './developers/TenantProvisioning';
import ThemeCustomizer from './developers/ThemeCustomizer';
import { ThemeProvider } from './theme/ThemeProvider';
import LogoUpload from './developers/LogoUpload';
import { TenantProvider, useTenant } from './context/TenantContext';
import { LicenseProvider } from './context/LicenseContext';
import { LicenseProtectedRoute } from './components/LicenseProtectedRoute';
import LicenseUpgrade from './components/LicenseUpgrade';
import { AuthProvider } from './context/AuthContext';
import Login from './components/Login';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';  // Add this import
import UserManagement from './components/UserManagement';
import NotificationSubscriptionManager from './components/notifications/NotificationSubscriptionManager';
import FeatureFlags from './developers/FeatureFlags';  // Add this import
import LocationDetails from './components/Install-base/LocationDetails';
import ServiceRequestsTable from './components/Install-base/ServiceRequestsTable';
import SimulationList from './components/DiscreteOrderSim/SimulationList';
import SimulationMap from './components/DiscreteOrderSim/SimulationMap';
import StoreTimeline from './components/DiscreteOrderSim/StoreTimeline';
import InvoiceProcessing from './components/InvoiceProcessing';



// Define feature constants
const DEVELOPER_FEATURES = {
  API_TEST: 'api-test',
  TENANT_MANAGEMENT: 'tenant-management',
  THEME_CUSTOMIZATION: 'theme-customization',
  FEATURE_FLAGS: 'feature-flags'
};

// Main App component
function App() {
  return (
    <Router>
      <TenantProvider>
        <AuthProvider>
          <ThemeProvider>
            <LicenseProvider>
              <CssBaseline />
              <FeatureFlagProvider>
                <AppContent />
              </FeatureFlagProvider>
            </LicenseProvider>
          </ThemeProvider>
        </AuthProvider>
      </TenantProvider>
    </Router>
  );
}

// Loading component with better feedback
function TenantLoading() {
  return (
    <div className="loading-container">
      <div className="spinner"></div>
      <p>Loading application...</p>
      <p className="loading-subtext">Resolving tenant information</p>
    </div>
  );
}

// Error component
function TenantError({ message, onRetry }) {
  return (
    <div className="error-container">
      <h1>Error Loading Application</h1>
      <p>{message || 'An unknown error occurred'}</p>
      <button onClick={onRetry}>Retry</button>
    </div>
  );
}

// App content with tenant and feature flag awareness
function AppContent() {
  const { tenantAlias, isLoading, error, refreshTenantAlias } = useTenant();
  const { isFeatureEnabled, isLoading: isFeaturesLoading, allFeatures } = useFeatureFlags();
  
  // Debug: Log features when they change
  useEffect(() => {
    if (!isFeaturesLoading) {
      console.log('Available features:', allFeatures);
    }
  }, [isFeaturesLoading, allFeatures]);
  
  // Show loading state while tenant is being resolved
  if (isLoading) {
    return <TenantLoading />;
  }
  
  // Show error state if tenant resolution failed
  if (error) {
    return <TenantError message={error} onRetry={refreshTenantAlias} />;
  }
  
  // Show error if no tenant was resolved
  if (!tenantAlias) {
    return <TenantError message="No tenant information found. Please check your URL." onRetry={refreshTenantAlias} />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Routes>
        {/* Login route - no layout */}
        <Route path="/login" element={<Login />} />
        
        {/* Protected routes with layout */}
        <Route element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          {/* Dashboard */}
          <Route path="/" element={<Dashboard />} />
          
          {/* Notification Settings */}
          <Route path="/notifications/subscriptions" element={<NotificationSubscriptionManager />} />
          
          {/* Product module routes */}
          {isFeatureEnabled(FEATURE_CODES.PRODUCT) && (
            <>
              <Route path="/product-master" element={<ProductMaster />} />
              <Route path="/product-supplier" element={<ProductSupplierView />} />
              <Route path="/product-hierarchy" element={<ProductHierarchyPage />} />
            </>
          )}
          
          {/* Forecasting module routes */}
          {isFeatureEnabled(FEATURE_CODES.FORECASTING) && (
            <>
              <Route path="/forecast-runs" element={<ForecastRuns />} />
              <Route path="/forecast-hierarchy/:runId" element={<ForecastCanvas />} />
              <Route path="/forecast-hierarchy/:runId/hierarchy/:hierarchyId/node/:nodeId" element={<NodeDetailsPage />} />
            </>
          )}
          
          {/* Inventory module routes */}
          {isFeatureEnabled(FEATURE_CODES.INVENTORY) && (
            <>
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/inventory/warehouse/:warehouseId" element={<WarehouseInventory />} />
              <Route path="/inventory/:sku/:lot" element={<InventorySkuLot />} />
              <Route path="/inventory-hierarchy" element={<InventoryHierarchyPage />} />
            </>
          )}
          
          {/* Configuration module routes */}
          {isFeatureEnabled(FEATURE_CODES.CONFIG) && (
            <Route path="/config" element={<ConfigManagement />} />
          )}
          
          {/* Order module routes */}
          {isFeatureEnabled(FEATURE_CODES.ORDER) && (
            <Route path="/orders" element={<Orders />} />
          )}
          
          {/* Network module routes */}
          {isFeatureEnabled(FEATURE_CODES.NETWORK) && (
            <>
              <Route path="/network" element={<NetworkMap />} />
              <Route path="/simulation" element={<SimulationList />} />
              <Route path="/simulation/map/:runId" element={<SimulationMap />} />
              <Route path="/simulation/timeline/:runId/:storeId" element={<StoreTimeline />} />
            </>
          )}
          
          {/* Install Base module route */}
          {isFeatureEnabled('FLAG_INSTALL_BASE_MODULE_001') && (
            <>
              <Route path="/install-base" element={<InstallBaseMap />} />
              <Route path="/install-base/customer/:customerId" element={<InstallBaseMap />} />
              <Route path="/installations/:installationId" element={<InstallationDetails />} />
              <Route path="/locations/:locationId" element={<LocationDetails />} />
            </>
          )}
          
          {/* Optimization module routes */}
          {isFeatureEnabled(FEATURE_CODES.OPTIMIZATION) && (
            <Route path="/inventory-optimization" element={<InventoryOptimization />} />
          )}
          
          {/* Shipment module routes */}
          {isFeatureEnabled(FEATURE_CODES.SHIPMENT) && (
            <Route path="/shipment-tracking" element={<ShipmentTrackingPage />} />
          )}
          
          {/* Developer routes */}
          <Route path="/developers" element={<DeveloperDashboard />} />
          <Route path="/developers/api-test" 
            element={
              <LicenseProtectedRoute feature={DEVELOPER_FEATURES.API_TEST}>
                <ApiTestPage />
              </LicenseProtectedRoute>
            } 
          />
          <Route path="/developers/tenant-management" element={
            <LicenseProtectedRoute feature={DEVELOPER_FEATURES.TENANT_MANAGEMENT}>
              <TenantManagement />
            </LicenseProtectedRoute>
          } />
          <Route path="/developers/feature-flags" element={
            <LicenseProtectedRoute>
              <FeatureFlags />
            </LicenseProtectedRoute>
          } />
          <Route path="/developers/tenant-features/:tenantAlias" element={
            <LicenseProtectedRoute>
              <TenantFeatures />
            </LicenseProtectedRoute>
          } />
          <Route path="/developers/user-management" element={<UserManagement />} />
          <Route path="/developers/theme-customization" element={<ThemeCustomizer />} />
          <Route path="/developers/tenant/create" element={<TenantCreate />} />
          <Route path="/developers/tenant-edit/:tenantAlias" element={<TenantEdit />} />
          <Route path="/developers/tenant-provisioning" element={<TenantProvisioning />} />
          <Route path="/developers/tenant/:tenantAlias/logo" element={<LogoUpload />} />
          
          {/* License upgrade route */}
          <Route path="/license-upgrade" element={<LicenseUpgrade />} />
          
          {/* Service Alerts route */}
          <Route path="/service-requests" element={<ServiceRequestsTable />} />
          
          {/* Invoice Processing route */}
          <Route path="/invoice-processing" element={<InvoiceProcessing />} />
        </Route>
      </Routes>
    </Box>
  );
}

export default App;